import type { Edge } from '@xyflow/react';

import { figureCatalogByKey } from './organogramaFigureCatalog';
import { EDGE_TIPOS, getEdgeTipo, strokeDasharrayForTipo, type OrgMvpEdgeTipo } from './organogramaGraph';
import { resolveColumnTitlePosition } from './organogramaTypes';
import type {
  OrgMvpAnnotationData,
  OrgMvpApiEdge,
  OrgMvpApiNode,
  OrgMvpCardData,
  OrgMvpColumnData,
  OrgMvpColumnTitlePosition,
  OrgMvpFigure,
  OrgMvpFigureData,
  OrgMvpNode,
  OrgMvpNodeKind,
  OrgMvpShapeData,
  OrgMvpShapeKind,
  OrgMvpTableData,
  OrgMvpTextData,
} from './organogramaTypes';

const COLUMN_TITLE_POSITIONS: OrgMvpColumnTitlePosition[] = ['left', 'top', 'bottom'];

const SHAPE_KINDS: OrgMvpShapeKind[] = ['square', 'circle', 'line'];

/** `dashed-line` foi uma forma própria na primeira versão de "Formas"; virou a forma
 *  `line` com o estilo de traço `dashed`. A leitura converte o valor antigo. */
const normalizeShape = (value: unknown, dashed: unknown) => {
  if (value === 'dashed-line') return { shape: 'line' as OrgMvpShapeKind, dashed: true };
  return {
    shape: SHAPE_KINDS.includes(value as OrgMvpShapeKind) ? (value as OrgMvpShapeKind) : 'square',
    dashed: dashed === true,
  };
};

/** Referência persistente da imagem no storage. Fica vazia quando a figura veio do
 *  `avatar_url` do usuário do sistema (que não é um upload do canvas). */
const uploadRefOf = (node: OrgMvpApiNode) =>
  node.figure_type === 'upload' && node.figure_storage_path && node.figure_bucket_name
    ? {
        storagePath: node.figure_storage_path,
        bucket: node.figure_bucket_name,
        mimeType: node.figure_mime_type || undefined,
      }
    : {};

export const mapApiNodesToFlow = (apiNodes: OrgMvpApiNode[]): OrgMvpNode[] => {
  return apiNodes
    .filter((node) => typeof node?.id === 'string' && node.id.length > 0)
    .map((node) => {
    const nodeType = ['column', 'figure', 'text', 'table', 'annotation', 'shape'].includes(node.node_type)
      ? node.node_type
      : 'card';
    const base = {
      id: node.id,
      type: nodeType,
      position: { x: Number(node.position_x || 0), y: Number(node.position_y || 0) },
      width: typeof node.width === 'number' ? node.width : undefined,
      height: typeof node.height === 'number' ? node.height : undefined,
      zIndex: Number(node.z_index || 0),
      style: {
        width: typeof node.width === 'number' ? node.width : undefined,
        height: typeof node.height === 'number' ? node.height : undefined,
      },
    } as OrgMvpNode;

    if (nodeType === 'column') {
      const json = (node.data_json || {}) as { titlePosition?: unknown };
      return {
        ...base,
        data: {
          title: node.label || 'Coluna',
          color: node.color || '#E0F2FE',
          orientation: node.orientation === 'horizontal' ? 'horizontal' : 'vertical',
          titlePosition: COLUMN_TITLE_POSITIONS.includes(json.titlePosition as OrgMvpColumnTitlePosition)
            ? (json.titlePosition as OrgMvpColumnTitlePosition)
            : undefined,
        },
      };
    }

    if (nodeType === 'text') {
      const json = (node.data_json || {}) as { text?: string; fontSize?: number };
      return {
        ...base,
        data: {
          text: typeof json.text === 'string' ? json.text : (node.label || ''),
          color: node.color || '#0F172A',
          fontSize: typeof json.fontSize === 'number' ? json.fontSize : 16,
        },
      };
    }

    if (nodeType === 'annotation') {
      const json = (node.data_json || {}) as { text?: string };
      return {
        ...base,
        data: {
          text: typeof json.text === 'string' ? json.text : (node.label || ''),
          color: node.color || '#FEF08A',
        },
      };
    }

    if (nodeType === 'table') {
      const json = (node.data_json || {}) as { rows?: string[][] };
      return {
        ...base,
        data: {
          rows: Array.isArray(json.rows) && json.rows.length > 0 ? json.rows : [['', ''], ['', '']],
        },
      };
    }

    if (nodeType === 'shape') {
      const json = (node.data_json || {}) as { shape?: unknown; dashed?: unknown; fill?: string; strokeWidth?: number };
      return {
        ...base,
        data: {
          ...normalizeShape(json.shape, json.dashed),
          color: node.color || '#334155',
          fill: typeof json.fill === 'string' ? json.fill : 'transparent',
          strokeWidth: typeof json.strokeWidth === 'number' ? json.strokeWidth : 2,
        },
      };
    }

    if (nodeType === 'figure') {
      const catalogExists = typeof node.figure_key === 'string' && figureCatalogByKey.has(node.figure_key);
      const uploadFigure = node.figure_type === 'upload' && typeof node.figure_url === 'string' && node.figure_url;
      return {
        ...base,
        data: {
          label: node.label || 'Figura',
          color: node.color || '#EA580C',
          showBackground: node.show_background !== false,
          figure: uploadFigure
            ? { type: 'upload', url: uploadFigure, name: node.label || 'Figura', ...uploadRefOf(node) }
            : { type: 'catalog', key: catalogExists ? node.figure_key : 'forklift' },
        },
      };
    }

    const kind: OrgMvpNodeKind = node.card_kind === 'department' ? 'department' : 'person';
    const cardCatalog = typeof node.figure_key === 'string' && figureCatalogByKey.has(node.figure_key)
      ? node.figure_key
      : (kind === 'person' ? 'person' : 'building');
    const ocultarFoto = (node.data_json as { ocultarFoto?: unknown } | null | undefined)?.ocultarFoto === true;
    const uploadFigure =
      (kind === 'person' && !ocultarFoto && typeof node.avatar_url === 'string' && node.avatar_url)
      || (node.figure_type === 'upload' && typeof node.figure_url === 'string' && node.figure_url)
      || null;
    return {
      ...base,
      data: {
        kind,
        label: node.label || (kind === 'person' ? 'Nova pessoa' : 'Novo departamento'),
        subtitle: node.subtitle || '',
        description: node.description || '',
        sourceType: node.source_type || (kind === 'person' ? 'ORG_ONLY' : undefined),
        usuarioId: node.usuario_id || null,
        ...(ocultarFoto ? { ocultarFoto: true } : {}),
        departamentoId: node.departamento_id || null,
        cronogramaId: node.cronograma_id || null,
        cronogramaTitulo: node.cronograma?.titulo || null,
        color: node.color || (kind === 'person' ? '#2563EB' : '#059669'),
        textColor: node.text_color || '#0F172A',
        figure: uploadFigure
          ? {
              type: 'upload',
              url: uploadFigure,
              name: node.label || (kind === 'person' ? 'Pessoa' : 'Departamento'),
              ...uploadRefOf(node),
            }
          : { type: 'catalog', key: cardCatalog },
      },
    };
    });
};

export const mapApiEdgesToFlow = (apiEdges: OrgMvpApiEdge[]): Edge[] => {
  return apiEdges
    .filter(
      (edge) =>
        typeof edge?.id === 'string'
        && edge.id.length > 0
        && typeof edge.source_node_id === 'string'
        && edge.source_node_id.length > 0
        && typeof edge.target_node_id === 'string'
        && edge.target_node_id.length > 0,
    )
    .map((edge) => {
      const tipo: OrgMvpEdgeTipo = EDGE_TIPOS.includes(edge.tipo as OrgMvpEdgeTipo)
        ? (edge.tipo as OrgMvpEdgeTipo)
        : 'direta';
      return {
        id: edge.id,
        source: edge.source_node_id,
        target: edge.target_node_id,
        sourceHandle: edge.source_handle || undefined,
        targetHandle: edge.target_handle || undefined,
        type: edge.edge_type || 'default',
        animated: Boolean(edge.animated),
        style: {
          stroke: edge.stroke || '#334155',
          strokeWidth: typeof edge.stroke_width === 'number' ? edge.stroke_width : 2.2,
        },
        data: { ...(edge.data_json || {}), tipo },
      };
    });
};

/**
 * Camada derivada de exibição: aplica o tracejado conforme o tipo da relação.
 * Não persiste — `mapFlowEdgesToApi` só envia stroke/strokeWidth do style.
 */
export const applyEdgeTipoStyle = (edge: Edge): Edge => {
  const strokeDasharray = strokeDasharrayForTipo(getEdgeTipo(edge));
  if (!strokeDasharray) return edge;
  return { ...edge, style: { ...(edge.style || {}), strokeDasharray } };
};

/** Só persiste como `upload` quando existe referência no storage. Uma figura `upload`
 *  sem storagePath veio do avatar do usuário do sistema — nesse caso volta ao catálogo. */
const figurePayload = (figure: OrgMvpFigure | undefined, fallbackKey: string) => {
  if (figure?.type === 'upload' && figure.storagePath && figure.bucket) {
    return {
      figureType: 'upload',
      figureKey: null as string | null,
      figureStoragePath: figure.storagePath,
      figureBucketName: figure.bucket,
      figureMimeType: figure.mimeType || null,
    };
  }
  return {
    figureType: 'catalog',
    figureKey: figure?.type === 'catalog' ? figure.key : fallbackKey,
    figureStoragePath: null as string | null,
    figureBucketName: null as string | null,
    figureMimeType: null as string | null,
  };
};

export const mapFlowNodesToApi = (flowNodes: OrgMvpNode[]) => {
  return flowNodes.map((node) => {
    const common = {
      id: node.id,
      nodeType: node.type,
      label: '',
      subtitle: '',
      description: '',
      color: '',
      textColor: '',
      figureType: null as string | null,
      figureKey: null as string | null,
      figureStoragePath: null as string | null,
      figureBucketName: null as string | null,
      figureMimeType: null as string | null,
      showBackground: true,
      orientation: null as 'vertical' | 'horizontal' | null,
      position: { x: node.position.x, y: node.position.y },
      width: typeof node.width === 'number' ? node.width : Number(node.style?.width) || null,
      height: typeof node.height === 'number' ? node.height : Number(node.style?.height) || null,
      zIndex: node.zIndex ?? 0,
      data: null as Record<string, unknown> | null,
    };

    if (node.type === 'column') {
      const data = node.data as OrgMvpColumnData;
      return {
        ...common,
        label: data.title || 'Coluna',
        color: data.color || '#E0F2FE',
        orientation: data.orientation || 'vertical',
        data: { titlePosition: resolveColumnTitlePosition(data) },
      };
    }

    if (node.type === 'text') {
      const data = node.data as OrgMvpTextData;
      return {
        ...common,
        label: data.text || '',
        color: data.color || '#0F172A',
        data: { text: data.text || '', fontSize: data.fontSize || 16 },
      };
    }

    if (node.type === 'annotation') {
      const data = node.data as OrgMvpAnnotationData;
      return {
        ...common,
        label: data.text || '',
        color: data.color || '#FEF08A',
        data: { text: data.text || '' },
      };
    }

    if (node.type === 'table') {
      const data = node.data as OrgMvpTableData;
      return {
        ...common,
        label: 'Tabela',
        data: { rows: Array.isArray(data.rows) ? data.rows : [['', ''], ['', '']] },
      };
    }

    if (node.type === 'shape') {
      const data = node.data as OrgMvpShapeData;
      return {
        ...common,
        label: 'Forma',
        color: data.color || '#334155',
        data: {
          ...normalizeShape(data.shape, data.dashed),
          fill: data.fill || 'transparent',
          strokeWidth: typeof data.strokeWidth === 'number' ? data.strokeWidth : 2,
        },
      };
    }

    if (node.type === 'figure') {
      const data = node.data as OrgMvpFigureData;
      return {
        ...common,
        label: data.label || 'Figura',
        color: data.color || '#EA580C',
        showBackground: data.showBackground !== false,
        ...figurePayload(data.figure, 'forklift'),
      };
    }

    const data = node.data as OrgMvpCardData;
    return {
      ...common,
      cardKind: data.kind === 'department' ? 'department' : 'person',
      sourceType: data.kind === 'person' ? (data.sourceType || 'ORG_ONLY') : null,
      usuarioId: data.usuarioId || null,
      departamentoId: data.departamentoId || null,
      cronogramaId: data.cronogramaId || null,
      label: data.label || '',
      subtitle: data.subtitle || '',
      description: data.description || '',
      color: data.color || '#2563EB',
      textColor: data.textColor || '#0F172A',
      ...figurePayload(data.figure, data.kind === 'person' ? 'person' : 'building'),
      // Única informação extra do card: a escolha de não mostrar a foto do usuário.
      data: data.ocultarFoto ? { ocultarFoto: true } : null,
    };
  });
};

export const mapFlowEdgesToApi = (flowEdges: Edge[]) => {
  return flowEdges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle || null,
    targetHandle: edge.targetHandle || null,
    edgeType: edge.type || 'default',
    tipo: getEdgeTipo(edge),
    stroke: typeof edge.style?.stroke === 'string' ? edge.style.stroke : null,
    strokeWidth: typeof edge.style?.strokeWidth === 'number' ? edge.style.strokeWidth : null,
    animated: Boolean(edge.animated),
    data: edge.data || null,
  }));
};
